/**
 * Canvas-based Avatar Component with Palette Swap
 * 
 * Renders avatar sprites with real-time color remapping:
 * 1. Loads base body sprite (gender × skin tone)
 * 2. Applies palette swap to change uniform colors
 * 3. Composites flair items on top
 * 
 * Uses canvas pixel manipulation for color remapping instead of CSS/SVG filters
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { SPRITE_ASSETS, SOURCE_UNIFORM_COLORS, FLAIR_ANCHORS } from '../assets/spriteAssets';

// Color distance threshold for palette matching (Euclidean distance in RGB space)
const COLOR_THRESHOLD = 60;

// Convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

// Calculate color distance
const colorDistance = (c1, c2) => {
  return Math.sqrt(
    Math.pow(c1.r - c2.r, 2) +
    Math.pow(c1.g - c2.g, 2) +
    Math.pow(c1.b - c2.b, 2)
  );
};

// Generate shaded color variants from a base color
const generateColorShades = (baseHex) => {
  const base = hexToRgb(baseHex);
  return {
    highlight: {
      r: Math.min(255, base.r + 40),
      g: Math.min(255, base.g + 40),
      b: Math.min(255, base.b + 40),
    },
    base: base,
    shadow: {
      r: Math.max(0, base.r - 40),
      g: Math.max(0, base.g - 40),
      b: Math.max(0, base.b - 40),
    },
    deep: {
      r: Math.max(0, base.r - 80),
      g: Math.max(0, base.g - 80),
      b: Math.max(0, base.b - 80),
    },
  };
};

// Check if a pixel matches a source color within threshold
const matchesSourceColor = (pixel, sourceShades, threshold = COLOR_THRESHOLD) => {
  for (const [shadeName, shadeColor] of Object.entries(sourceShades)) {
    if (colorDistance(pixel, shadeColor) < threshold) {
      return shadeName;
    }
  }
  return null;
};

// Apply palette swap to image data
const applyPaletteSwap = (imageData, primaryColor, secondaryColor, accentColor) => {
  const data = imageData.data;
  
  const targetPrimary = generateColorShades(primaryColor);
  const targetSecondary = generateColorShades(secondaryColor);
  const targetAccent = generateColorShades(accentColor);
  
  for (let i = 0; i < data.length; i += 4) {
    const pixel = { r: data[i], g: data[i + 1], b: data[i + 2] };
    const alpha = data[i + 3];
    
    // Skip fully transparent pixels
    if (alpha < 10) continue;
    
    // Check primary colors (jersey/cap/socks - greens)
    const primaryMatch = matchesSourceColor(pixel, SOURCE_UNIFORM_COLORS.primary);
    if (primaryMatch) {
      const newColor = targetPrimary[primaryMatch];
      data[i] = newColor.r;
      data[i + 1] = newColor.g;
      data[i + 2] = newColor.b;
      continue;
    }
    
    // Check accent colors (trim/piping - golds)
    const accentMatch = matchesSourceColor(pixel, SOURCE_UNIFORM_COLORS.accent, 50);
    if (accentMatch) {
      const newColor = targetAccent[accentMatch] || targetAccent.base;
      data[i] = newColor.r;
      data[i + 1] = newColor.g;
      data[i + 2] = newColor.b;
      continue;
    }
  }
  
  return imageData;
};

export default function AvatarCanvas({ 
  config, 
  size = 128, 
  showFlair = true,
  className = '',
}) {
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const {
    gender = 'male',
    skin_tone = 'medium',
    primary_color = '#4B8B3B',
    secondary_color = '#F5F5DC',
    accent_color = '#FFD700',
    equipped_flair = {}
  } = config || {};

  const renderAvatar = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const spriteUrl = SPRITE_ASSETS[gender]?.[skin_tone];
    
    if (!spriteUrl) {
      setError(`No sprite for ${gender}/${skin_tone}`);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Load base sprite
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load sprite'));
        img.src = spriteUrl;
      });
      
      // Set canvas size and draw base image
      const aspectRatio = img.height / img.width;
      canvas.width = size;
      canvas.height = size * aspectRatio;
      
      ctx.imageSmoothingEnabled = false; // Keep pixelated look
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Apply palette swap
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const swappedData = applyPaletteSwap(
        imageData, 
        primary_color, 
        secondary_color, 
        accent_color
      );
      ctx.putImageData(swappedData, 0, 0);
      
      // Composite flair items if enabled
      if (showFlair && equipped_flair) {
        await compositeFlair(ctx, canvas, equipped_flair, gender);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Avatar render error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [gender, skin_tone, primary_color, secondary_color, accent_color, equipped_flair, showFlair, size]);

  // Composite flair items on top using isolated sprites with anchor positions
  const compositeFlair = async (ctx, canvas, flair, gender) => {
    const anchors = FLAIR_ANCHORS[gender] || FLAIR_ANCHORS.male;
    const flairItems = [];
    
    // Map flair slots to sprite keys and anchors
    if (flair.head === 'sunglasses' && SPRITE_ASSETS.flair.sunglasses) {
      flairItems.push({ 
        url: SPRITE_ASSETS.flair.sunglasses, 
        anchor: anchors.sunglasses,
        slot: 'head'
      });
    }
    if (flair.neck === 'gold_chain' && SPRITE_ASSETS.flair.gold_chain) {
      flairItems.push({ 
        url: SPRITE_ASSETS.flair.gold_chain, 
        anchor: anchors.gold_chain,
        slot: 'neck'
      });
    }
    if (flair.feet === 'neon_cleats' && SPRITE_ASSETS.flair.neon_cleats) {
      flairItems.push({ 
        url: SPRITE_ASSETS.flair.neon_cleats, 
        anchor: anchors.neon_cleats,
        slot: 'feet'
      });
    }
    if (flair.held === 'bat' && SPRITE_ASSETS.flair.bat) {
      flairItems.push({ 
        url: SPRITE_ASSETS.flair.bat, 
        anchor: anchors.bat,
        slot: 'held'
      });
    }
    if (flair.held === 'glove' && SPRITE_ASSETS.flair.glove) {
      flairItems.push({ 
        url: SPRITE_ASSETS.flair.glove, 
        anchor: anchors.glove,
        slot: 'held'
      });
    }
    
    // Load and composite each flair item
    for (const item of flairItems) {
      try {
        const flairImg = new Image();
        flairImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          flairImg.onload = resolve;
          flairImg.onerror = () => reject(new Error(`Failed to load flair: ${item.slot}`));
          flairImg.src = item.url;
        });
        
        // Calculate size maintaining aspect ratio
        // Use width as the constraint, calculate height from sprite's natural aspect ratio
        const targetWidth = canvas.width * item.anchor.width;
        const aspectRatio = flairImg.height / flairImg.width;
        const targetHeight = targetWidth * aspectRatio;
        
        // Position: x is left edge, y centers the sprite at the anchor point
        const x = canvas.width * item.anchor.x;
        const y = (canvas.height * item.anchor.y) - (targetHeight / 2);
        
        // Draw flair item with pixelated rendering
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(flairImg, x, y, targetWidth, targetHeight);
      } catch (err) {
        console.warn(`Flair composite error (${item.slot}):`, err.message);
      }
    }
  };

  useEffect(() => {
    renderAvatar();
  }, [renderAvatar]);

  return (
    <div 
      className={`avatar-canvas-container ${className}`}
      style={{ 
        width: size, 
        display: 'inline-block',
        position: 'relative',
      }}
      data-testid="avatar-canvas"
    >
      {isLoading && (
        <div 
          style={{ 
            width: size, 
            height: size * 1.2, 
            background: 'rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#F5F5DC',
            fontSize: '10px',
            overflow: 'hidden',
          }}
        >
          {size >= 48 ? 'Loading...' : ''}
        </div>
      )}
      {error && (
        <div 
          style={{ 
            width: size, 
            height: size * 1.2, 
            background: 'rgba(200,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#F5F5DC',
            fontSize: '10px',
            padding: '4px',
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}
      <canvas 
        ref={canvasRef}
        style={{ 
          imageRendering: 'pixelated',
          display: isLoading || error ? 'none' : 'block',
          width: '100%',
          height: 'auto',
        }}
      />
    </div>
  );
}

// Small avatar variant
export function AvatarCanvasSmall({ config, size = 48, className = '' }) {
  return (
    <AvatarCanvas 
      config={config} 
      size={size} 
      showFlair={false}
      className={className}
    />
  );
}

// Avatar with level badge
export function AvatarCanvasWithLevel({ config, level, size = 128, className = '' }) {
  return (
    <div className={`relative inline-block ${className}`}>
      <AvatarCanvas config={config} size={size} />
      <div 
        className="absolute -bottom-1 -right-1 bg-grass text-cream text-xs font-bold px-2 py-0.5 border-2 border-ink rounded"
        style={{ fontFamily: "'Press Start 2P', cursive", fontSize: '8px' }}
      >
        Lv{level}
      </div>
    </div>
  );
}
